import React from 'react';
import { Link } from 'react-router-dom';
import MetaTags, { getBrandPageMetaData } from '../ui/MetaTags';
import {
  BRAND_ALTERNATE_NAMES,
  BRAND_CANONICAL_LEAD,
  BRAND_FAQ_ITEMS,
} from '../../data/brandSeoCopy';
import './ContentPage.scss';

const ASSET_BASE = 'https://www.elodiecarstensen.com/assets/brand';

const BrandPage = () => {
  const meta = getBrandPageMetaData();

  return (
    <>
      <MetaTags {...meta}>
        <link rel="canonical" href="https://www.elodiecarstensen.com/brand" />
      </MetaTags>
      <article className="content-page brand-page">
        <header className="brand-page-header">
          <h1>Elodie Carstensen — brand, logo &amp; facts</h1>
          <p className="brand-lead">
            {BRAND_CANONICAL_LEAD}
          </p>
          <p className="brand-crosslink">
            For the immersive timeline, events, and press slides, open{' '}
            <Link to="/about">the About experience</Link>
            {' '}(<Link to="/about/cv">CV / timeline overview</Link>).
          </p>
        </header>

        <section aria-labelledby="entity-facts-heading">
          <h2 id="entity-facts-heading">Brand entity (machine-readable summary)</h2>
          <p>
            <strong>Official name:</strong> Elodie Carstensen (fashion label).
          </p>
          <p>
            <strong>Also known as / alternate phrasing:</strong>{' '}
            {BRAND_ALTERNATE_NAMES.join('; ')}.
          </p>
          <p>
            <strong>Location:</strong> Berlin, Germany — studio address Kadiner Str. 20A, 10243 Berlin.
          </p>
          <p>
            <strong>Contact:</strong>{' '}
            <a href="mailto:info@elodiecarstensen.com">info@elodiecarstensen.com</a>
          </p>
          <p>
            <strong>Official social:</strong>{' '}
            <a href="https://www.instagram.com/elodiecarstensen/" rel="me noopener noreferrer">
              @elodiecarstensen on Instagram
            </a>
          </p>
          <p>
            <strong>Symbol:</strong> The official monogram is the <strong>crown mark</strong> (crown logo).
            It is not a generic “decorative crown”; in context with the Elodie Carstensen name it identifies this label.
          </p>
        </section>

        <section aria-labelledby="downloads-heading">
          <h2 id="downloads-heading">Official logo &amp; monogram downloads</h2>
          <p>
            Master artwork is <strong>SVG</strong> (vector). For social platforms, retailers, and image search, we also
            publish <strong>PNG exports</strong> generated from those SVGs (same geometry — use SVG when you need
            infinite scaling).
          </p>
          <p>
            Use these descriptive file names in articles, marketplaces, and archives so search and assistants can match
            images to the brand. Pair each file with nearby explanatory text (as on this page).
          </p>
          <h3 className="brand-downloads-sub">Crown monogram — SVG (source)</h3>
          <ul className="brand-asset-list">
            <li>
              <a
                href={`${ASSET_BASE}/elodie-carstensen-crown-monogram-white-on-black.svg`}
                download
              >
                elodie-carstensen-crown-monogram-white-on-black.svg
              </a>
              {' — '}
              Crown monogram, white on black (SVG)
            </li>
            <li>
              <a
                href={`${ASSET_BASE}/elodie-carstensen-crown-monogram-black-on-white.svg`}
                download
              >
                elodie-carstensen-crown-monogram-black-on-white.svg
              </a>
              {' — '}
              Crown monogram, black on white (SVG)
            </li>
          </ul>
          <h3 className="brand-downloads-sub">Crown monogram — PNG (export)</h3>
          <ul className="brand-asset-list">
            <li>
              <a href={`${ASSET_BASE}/elodie-carstensen-crown-monogram-white-on-black-512.png`} download>
                elodie-carstensen-crown-monogram-white-on-black-512.png
              </a>
              {' — '}
              512×512, white on black
            </li>
            <li>
              <a href={`${ASSET_BASE}/elodie-carstensen-crown-monogram-white-on-black-1024.png`} download>
                elodie-carstensen-crown-monogram-white-on-black-1024.png
              </a>
              {' — '}
              1024×1024, white on black
            </li>
            <li>
              <a href={`${ASSET_BASE}/elodie-carstensen-crown-monogram-black-on-white-512.png`} download>
                elodie-carstensen-crown-monogram-black-on-white-512.png
              </a>
              {' — '}
              512×512, black on white
            </li>
            <li>
              <a href={`${ASSET_BASE}/elodie-carstensen-crown-monogram-black-on-white-1024.png`} download>
                elodie-carstensen-crown-monogram-black-on-white-1024.png
              </a>
              {' — '}
              1024×1024, black on white
            </li>
          </ul>
          <h3 className="brand-downloads-sub">Wordmark — PNG (export from logo.svg)</h3>
          <ul className="brand-asset-list">
            <li>
              <a href={`${ASSET_BASE}/elodie-carstensen-wordmark-white-on-dark-1200w.png`} download>
                elodie-carstensen-wordmark-white-on-dark-1200w.png
              </a>
              {' — '}
              Wide banner (1200×320), white wordmark on dark — for headers and retailer sheets
            </li>
            <li>
              <a href={`${ASSET_BASE}/elodie-carstensen-brand-og-1200x630.png`} download>
                elodie-carstensen-brand-og-1200x630.png
              </a>
              {' — '}
              Open Graph / link preview 1200×630 (crown + caption)
            </li>
          </ul>
        </section>

        <section aria-labelledby="previews-heading">
          <h2 id="previews-heading">Image previews (with alt text)</h2>
          <figure className="brand-figure">
            <img
              src="/assets/brand/elodie-carstensen-crown-monogram-white-on-black.svg"
              width={320}
              height={320}
              loading="lazy"
              decoding="async"
              alt="Official Elodie Carstensen crown monogram: geometric crown logo in white on a black square — vector brand asset for dark backgrounds"
            />
            <figcaption>
              Official Elodie Carstensen crown monogram (white on black) — use on dark UI, black invitations, or
              high-contrast press layouts.
            </figcaption>
          </figure>
          <figure className="brand-figure">
            <img
              src="/assets/brand/elodie-carstensen-crown-monogram-black-on-white.svg"
              width={320}
              height={320}
              loading="lazy"
              decoding="async"
              alt="Official Elodie Carstensen crown monogram: geometric crown logo in black on a white square — vector brand asset for light backgrounds"
            />
            <figcaption>
              Official crown monogram (black on white) — use on light paper, lookbooks, and retailer sheets.
            </figcaption>
          </figure>
          <figure className="brand-figure brand-figure--raster">
            <img
              src="/assets/brand/elodie-carstensen-crown-monogram-white-on-black-512.png"
              width={512}
              height={512}
              loading="lazy"
              decoding="async"
              alt="Official Elodie Carstensen crown monogram PNG export: 512 by 512 pixels, white geometric crown on black square"
            />
            <figcaption>
              PNG export (512×512) — same artwork as the SVG; use for apps and platforms that prefer raster logos.
            </figcaption>
          </figure>
          <figure className="brand-figure brand-figure--raster brand-figure--wordmark">
            <img
              src="/assets/brand/elodie-carstensen-wordmark-white-on-dark-1200w.png"
              width={1200}
              height={320}
              loading="lazy"
              decoding="async"
              alt="Elodie Carstensen wordmark PNG: white horizontal wordmark on dark background, exported from official logo.svg"
            />
            <figcaption>
              Wordmark banner (PNG) — derived from <code>assets/images/logo.svg</code> for contexts that need a raster
              wordmark.
            </figcaption>
          </figure>
          <figure className="brand-figure brand-figure--raster">
            <img
              src="/assets/brand/elodie-carstensen-brand-og-1200x630.png"
              width={1200}
              height={630}
              loading="lazy"
              decoding="async"
              alt="Elodie Carstensen brand preview image: crown monogram on dark background with title text for social sharing"
            />
            <figcaption>
              Open Graph preview (1200×630) — same asset used for link previews when this page is shared.
            </figcaption>
          </figure>
        </section>

        <section aria-labelledby="glossary-heading">
          <h2 id="glossary-heading">Short glossary (citable)</h2>
          <dl className="brand-glossary">
            <dt>Demi-couture</dt>
            <dd>
              In this context: made-to-order and craft-forward fashion between prêt-à-porter and full haute couture,
              with strong narrative and installation-led presentation.
            </dd>
            <dt>Crown monogram</dt>
            <dd>
              The registered-style visual mark of the Elodie Carstensen label: a geometric crown used with the brand
              name on labels and official media.
            </dd>
            <dt>Neck label</dt>
            <dd>
              The interior garment label where the Elodie Carstensen name and crown monogram typically appear together on
              authentic pieces.
            </dd>
          </dl>
        </section>

        <section aria-labelledby="faq-heading" id="faq">
          <h2 id="faq-heading">FAQ</h2>
          {BRAND_FAQ_ITEMS.map(({ id, heading, answer }) => (
            <div key={id} className="brand-faq-block">
              <h3>{heading}</h3>
              <p>{answer}</p>
            </div>
          ))}
        </section>

        <footer className="brand-page-footer">
          <p>
            <Link to="/">Collections &amp; home</Link>
            {' · '}
            <Link to="/about">About (immersive)</Link>
            {' · '}
            <Link to="/imprint">Imprint</Link>
          </p>
        </footer>
      </article>
    </>
  );
};

export default BrandPage;
